<template>
  <div class="ri-page bg-light-page">

    <!-- Header -->
    <div class="ri-header d-flex align-items-center">
      <button class="ri-back-btn d-flex align-items-center justify-content-center" type="button" @click="$router.back()">
        <ArrowLeft :size="20" :stroke-width="2.5" />
      </button>
      <h1 class="heading-6-1 mb-0">Report an Issue</h1>
    </div>

    <!-- Screen 1: Form -->
    <div v-if="screen === 'form'" class="ri-scroll">

      <p class="ri-subtitle px-3">Select the facility that needs attention</p>

      <!-- Facility grid -->
      <div class="ri-grid px-3">
        <button
          v-for="f in FACILITIES"
          :key="f.id"
          type="button"
          :class="['ri-tile', { 'ri-tile--selected': selectedFacility && selectedFacility.id === f.id }]"
          @click="selectFacility(f)"
        >
          <div
            class="ri-tile-icon d-flex align-items-center justify-content-center"
            :class="{ 'ri-tile-icon--selected': selectedFacility && selectedFacility.id === f.id }"
            :style="selectedFacility && selectedFacility.id === f.id ? {} : { color: f.color, borderColor: f.color + '30' }"
          >
            <component :is="f.icon" :size="22" :stroke-width="1.8" />
          </div>
          <span class="ri-tile-label">{{ f.label }}</span>
        </button>
      </div>

      <!-- Critical banner -->
      <transition name="ri-fade">
        <div v-if="isCritical" class="ri-critical px-3 mt-3">
          <div class="ri-critical-inner d-flex align-items-start">
            <AlertTriangle :size="16" :stroke-width="2.5" class="ri-critical-icon flex-shrink-0 mt-1 mr-2" />
            <div class="flex-grow-1">
              <p class="ri-critical-text mb-2">This issue requires immediate attention. You may also contact the front desk directly.</p>
              <button type="button" class="ri-call-btn">Call Front Desk</button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Form detail -->
      <div class="ri-form px-3 mt-3">
        <p class="ri-form-label mb-1">Describe the issue <span class="ri-optional">(optional)</span></p>
        <textarea
          v-model="description"
          class="ri-textarea w-100"
          rows="3"
          placeholder="e.g. The AC is making a loud noise and not cooling the room"
        ></textarea>

        <p class="ri-form-label mt-3 mb-2">Add Photo <span class="ri-optional">(optional, max 3)</span></p>
        <div class="ri-photo-row d-flex">
          <div v-for="(photo, i) in photos" :key="photo.preview" class="ri-photo-thumb position-relative mr-2">
            <img :src="photo.preview" class="ri-photo-img" />
            <button type="button" class="ri-photo-remove d-flex align-items-center justify-content-center" @click="removePhoto(i)">
              <X :size="9" :stroke-width="3" />
            </button>
          </div>
          <label v-if="photos.length < 3" class="ri-photo-add d-flex flex-column align-items-center justify-content-center" for="ri-file-input">
            <Plus :size="22" :stroke-width="2" class="ri-photo-plus" />
            <input
              ref="fileInput"
              type="file"
              id="ri-file-input"
              accept="image/png,image/jpeg,image/jpg"
              class="d-none"
              @change="addPhoto"
            />
          </label>
        </div>
      </div>

      <div style="height: 96px"></div>
    </div>

    <!-- Sticky submit bar -->
    <div v-if="screen === 'form'" class="ri-sticky-bar">
      <p v-if="submitError" class="ri-submit-error mb-3">{{ submitError }}</p>
      <button
        class="ri-submit-btn w-100"
        type="button"
        :disabled="!selectedFacility || isSubmitting"
        @click="submit"
      >
        {{ isSubmitting ? 'Submitting...' : 'Submit Report' }}
      </button>
    </div>

    <!-- Screen 2: Success -->
    <transition name="ri-fade">
      <div v-if="screen === 'success'" class="ri-success d-flex flex-column align-items-center px-4">
        <div class="ri-success-icon d-flex align-items-center justify-content-center mb-4">
          <CheckCircle :size="56" :stroke-width="1.5" />
        </div>
        <h2 class="ri-success-title text-center mb-2">Issue Reported!</h2>
        <p class="ri-success-sub text-center mb-4">
          Our team will look into your <strong>{{ submittedFacility.label }}</strong> issue shortly.
        </p>

        <div v-if="submittedResult" class="ri-result-card w-100 mb-5">
          <!-- Invoice -->
          <div class="ri-result-row d-flex align-items-center justify-content-between">
            <span class="ri-result-label">Invoice</span>
            <span class="ri-result-invoice">{{ submittedResult.invoice }}</span>
          </div>

          <!-- Room -->
          <div
            v-if="submittedResult.ga_reservation && submittedResult.ga_reservation.room"
            class="ri-result-row d-flex align-items-center justify-content-between"
          >
            <span class="ri-result-label">Room</span>
            <span class="ri-result-value">{{ submittedResult.ga_reservation.room.name }}</span>
          </div>

          <!-- Facility -->
          <div class="ri-result-row d-flex align-items-center justify-content-between">
            <span class="ri-result-label">Facility</span>
            <span class="ri-result-value">{{ submittedFacility.label }}</span>
          </div>

          <!-- Status -->
          <div class="ri-result-row d-flex align-items-center justify-content-between">
            <span class="ri-result-label">Status</span>
            <span class="ri-status-chip">{{ submittedResult.service_status }}</span>
          </div>

          <!-- Notes -->
          <div v-if="submittedDescription" class="ri-result-divider"></div>
          <div v-if="submittedDescription" class="ri-result-notes">
            <p class="ri-result-label mb-1">Note</p>
            <p class="mb-0 ri-result-note-text">{{ submittedDescription }}</p>
          </div>

          <!-- Uploaded images -->
          <template v-if="submittedResult.image_list && submittedResult.image_list.length">
            <div class="ri-result-divider"></div>
            <p class="ri-result-label mb-2">Photos</p>
            <div class="ri-result-photos d-flex">
              <img
                v-for="img in submittedResult.image_list"
                :key="img.id"
                :src="img.image_url"
                class="ri-result-photo mr-2"
              />
            </div>
          </template>
        </div>
        <div v-else class="mb-5"></div>

        <button class="ri-submit-btn w-100 mb-3" type="button" @click="backToHome">
          Back to Home
        </button>
        <button class="ri-outline-btn w-100" type="button" @click="reportAnother">
          Report Another Issue
        </button>
      </div>
    </transition>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { post } from '@/services/index.js'
import {
  ArrowLeft,
  Wind,
  Tv,
  Wifi,
  Droplet,
  Lightbulb,
  Lock,
  Shield,
  Wrench,
  Zap,
  AlertTriangle,
  CheckCircle,
  X,
  Plus,
} from 'lucide-vue'

const FACILITIES = [
  { id: 'ac',          label: 'AC',          icon: Wind,      color: '#2A7EC8' },
  { id: 'tv',          label: 'TV',          icon: Tv,        color: '#6A4AC8' },
  { id: 'wifi',        label: 'WiFi',        icon: Wifi,      color: '#1B9E77' },
  { id: 'hot-water',   label: 'Hot Water',   icon: Droplet,   color: '#2B7AB8' },
  { id: 'lamp',        label: 'Lamp',        icon: Lightbulb, color: '#C0871A' },
  { id: 'door-lock',   label: 'Door Lock',   icon: Lock,      color: '#C04A30' },
  { id: 'safe-box',    label: 'Safe Box',    icon: Shield,    color: '#3A8A48' },
  { id: 'plumbing',    label: 'Plumbing',    icon: Wrench,    color: '#7B6030' },
  { id: 'electricity', label: 'Electricity', icon: Zap,       color: '#C0871A' },
]

const CRITICAL_IDS = new Set(['door-lock', 'electricity'])

export default {
  name: 'ReportIssue',

  components: {
    ArrowLeft, Wind, Tv, Wifi, Droplet, Lightbulb, Lock, Shield, Wrench, Zap,
    AlertTriangle, CheckCircle, X, Plus,
  },

  metaInfo() {
    return {
      title: 'Report an Issue',
      titleTemplate: this.getMetaTitleTemplate(),
    }
  },

  data() {
    return {
      FACILITIES,
      screen: 'form',
      selectedFacility: null,
      description: '',
      photos: [],
      submittedFacility: null,
      submittedDescription: '',
      submittedResult: null,
      isSubmitting: false,
      submitError: '',
    }
  },

  computed: {
    isCritical() {
      return this.selectedFacility && CRITICAL_IDS.has(this.selectedFacility.id)
    },
  },

  mounted() {
    var auth = this.getConciergeHomeAuth()
    if (auth.reservation_status && auth.reservation_status !== 'CHECKIN') {
      this.$router.replace({ name: 'ConciergeMainPage', query: { toast: 'Available after check-in' } })
    }
  },

  beforeDestroy() {
    this.photos.forEach(p => URL.revokeObjectURL(p.preview))
  },

  methods: {
    ...mapGetters(['getMerchant', 'getMetaTitleTemplate', 'getConciergeHomeAuth']),

    selectFacility(f) {
      this.selectedFacility = f
    },

    addPhoto(e) {
      const file = e.target.files[0]
      if (!file || this.photos.length >= 3) return
      this.photos.push({ file, preview: URL.createObjectURL(file) })
      e.target.value = ''
    },

    removePhoto(i) {
      URL.revokeObjectURL(this.photos[i].preview)
      this.photos.splice(i, 1)
    },

    submit() {
      if (!this.selectedFacility || this.isSubmitting) return
      this.isSubmitting = true
      this.submitError = ''

      var auth = this.getConciergeHomeAuth()
      var facilityCode = this.selectedFacility.label.toUpperCase().replace(/ /g, '_')

      var fd = new FormData()
      fd.append('reservation_id', auth.reservation_id)
      fd.append('merchant_id', this.getMerchant().id)
      fd.append('module_slug', 'report-issue')
      fd.append('facility', facilityCode)
      fd.append('notes', this.description.trim())
      this.photos.forEach(function(photo) {
        fd.append('image', photo.file)
      })

      post('CONCIERGE', 'service/tx-inquiry', fd, { type: 'concierge_token_direct', token: auth.token })
        .then(function(response) {
          var res = response.data
          if (res.success) {
            this.submittedFacility = this.selectedFacility
            this.submittedDescription = this.description.trim()
            this.submittedResult = res.data
            this.$nextTick(function() { this.screen = 'success' }.bind(this))
          } else {
            this.submitError = res.message || 'Failed to submit report.'
          }
          this.isSubmitting = false
        }.bind(this))
        .catch(function() {
          this.submitError = 'Something went wrong. Please try again.'
          this.isSubmitting = false
        }.bind(this))
    },

    backToHome() {
      this.$router.push({ name: 'ConciergeMainPage' })
    },

    reportAnother() {
      this.photos.forEach(p => URL.revokeObjectURL(p.preview))
      this.selectedFacility = null
      this.description = ''
      this.photos = []
      this.submittedFacility = null
      this.submittedDescription = ''
      this.submittedResult = null
      this.submitError = ''
      this.screen = 'form'
    },
  },
}
</script>

<style scoped>
/* ── Page & layout ─────────────────────────── */
.ri-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* ── Header ────────────────────────────────── */
.ri-header {
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid #E8EEF3;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 12px;
}

.ri-back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #E0E8EF;
  background: #F5F7FA;
  color: #1C2B36;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

/* ── Scroll area ───────────────────────────── */
.ri-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: 20px;
}

.ri-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #6B7886;
  margin-bottom: 14px;
  line-height: 1.4;
}

/* ── Facility Grid ─────────────────────────── */
.ri-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 0;
}

.ri-tile {
  background: #fff;
  border: 1.5px solid #E8EEF3;
  border-radius: 16px;
  padding: 14px 6px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.12s ease, border-color 0.15s, background 0.15s;
  box-shadow: 0 1px 2px rgba(40, 45, 55, 0.04), 0 3px 8px rgba(40, 45, 55, 0.05);
  -webkit-tap-highlight-color: transparent;
}

.ri-tile:active {
  transform: scale(0.94);
}

.ri-tile--selected {
  background: #EEF5F9;
  border-color: var(--merchant-primary-color, #2B5F7A);
}

.ri-tile-icon {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: #F3F4F6;
  border: 1px solid transparent;
  flex-shrink: 0;
  color: #1C2030;
  transition: background 0.15s, color 0.15s;
}

.ri-tile-icon--selected {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border-color: transparent;
}

.ri-tile-label {
  font-size: 10px;
  font-weight: 800;
  color: #1C2030;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.2;
}

.ri-tile--selected .ri-tile-label {
  color: var(--merchant-primary-color, #2B5F7A);
}

/* ── Critical Banner ───────────────────────── */
.ri-critical {
  margin-bottom: 0;
}

.ri-critical-inner {
  background: #FFF8E6;
  border: 1.5px solid #F0C060;
  border-radius: 14px;
  padding: 12px 14px;
}

.ri-critical-icon {
  color: #D97B10;
}

.ri-critical-text {
  font-size: 12.5px;
  font-weight: 600;
  color: #7A4800;
  line-height: 1.5;
  margin: 0;
}

.ri-call-btn {
  font-size: 12px;
  font-weight: 800;
  color: #D97B10;
  background: transparent;
  border: 1.5px solid #D97B10;
  border-radius: 8px;
  padding: 5px 12px;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
}

.ri-call-btn:active {
  background: rgba(217, 123, 16, 0.08);
}

/* ── Form section ──────────────────────────── */
.ri-form {
  padding-bottom: 4px;
}

.ri-form-label {
  font-size: 13px;
  font-weight: 700;
  color: #1C2B36;
  margin: 0;
}

.ri-optional {
  font-weight: 500;
  color: #8896A4;
}

.ri-textarea {
  border: 1.5px solid #E0E8EF;
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 13.5px;
  color: #1C2B36;
  background: #fff;
  resize: none;
  font-family: inherit;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.15s;
}

.ri-textarea:focus {
  border-color: var(--merchant-primary-color, #2B5F7A);
}

/* ── Photo upload ──────────────────────────── */
.ri-photo-row {
  flex-wrap: wrap;
  gap: 10px;
}

.ri-photo-thumb {
  width: 72px;
  height: 72px;
  border-radius: 12px;
  overflow: visible;
  flex-shrink: 0;
}

.ri-photo-img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 12px;
  display: block;
}

.ri-photo-remove {
  position: absolute;
  top: -7px;
  right: -7px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #fff;
  border: 1.5px solid #E0E8EF;
  color: #1C2B36;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
  -webkit-tap-highlight-color: transparent;
  padding: 0;
}

.ri-photo-add {
  width: 72px;
  height: 72px;
  border-radius: 12px;
  border: 1.5px dashed #C0CDD8;
  background: #F5F7FA;
  cursor: pointer;
  color: #8896A4;
  flex-shrink: 0;
  transition: border-color 0.12s, background 0.12s;
  -webkit-tap-highlight-color: transparent;
}

.ri-photo-add:active {
  background: #EDF1F5;
}

/* ── Sticky bar ────────────────────────────── */
.ri-sticky-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  padding: 14px 16px 22px;
  border-top: 1px solid #E8EEF3;
  z-index: 20;
}

/* ── Submit error ──────────────────────────── */
.ri-submit-error {
  font-size: 13px;
  color: #C0392B;
  background: #FEF0EE;
  border: 1px solid #F5C6C0;
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.5;
}

/* ── Buttons ───────────────────────────────── */
.ri-submit-btn {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 15px 20px;
  font-size: 15px;
  cursor: pointer;
  font-family: Nunito-Bold, inherit;
  letter-spacing: 0.2px;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.12s;
}

.ri-submit-btn:disabled {
  background: #C8D4DC;
  cursor: default;
}

.ri-submit-btn:not(:disabled):active {
  filter: brightness(0.9);
}

.ri-outline-btn {
  background: transparent;
  color: var(--merchant-primary-color, #2B5F7A);
  border: 2px solid var(--merchant-primary-color, #2B5F7A);
  border-radius: 14px;
  padding: 13px 20px;
  font-size: 15px;
  cursor: pointer;
  font-family: Nunito-Bold, inherit;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
}

.ri-outline-btn:active {
  background: rgba(43, 95, 122, 0.07);
}

/* ── Screen 2: Success ─────────────────────── */
.ri-success {
  flex: 1;
  padding-top: 64px;
  padding-bottom: 40px;
}

.ri-success-icon {
  width: 96px;
  height: 96px;
  border-radius: 999px;
  background: #E6F5EC;
  color: #1A8F44;
}

.ri-success-title {
  font-size: 24px;
  font-weight: 900;
  color: #1C2B36;
  letter-spacing: -0.4px;
}

.ri-success-sub {
  font-size: 14px;
  color: #6B7886;
  line-height: 1.6;
}

.ri-result-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(15, 30, 50, 0.05);
}

.ri-result-row {
  padding: 7px 0;
}

.ri-result-row + .ri-result-row {
  border-top: 1px solid #F0F4F7;
}

.ri-result-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #8896A4;
}

.ri-result-value {
  font-size: 13.5px;
  font-weight: 700;
  color: #1C2B36;
}

.ri-result-invoice {
  font-size: 13px;
  font-weight: 800;
  color: var(--merchant-primary-color, #2B5F7A);
  letter-spacing: 0.3px;
}

.ri-status-chip {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #C08010;
  background: #FFF4DC;
  border: 1px solid #F0CC70;
  border-radius: 20px;
  padding: 3px 10px;
}

.ri-result-divider {
  height: 1px;
  background: #F0F4F7;
  margin: 10px 0;
}

.ri-result-notes {
  padding: 2px 0 4px;
}

.ri-result-note-text {
  font-size: 13.5px;
  color: #1C2B36;
  line-height: 1.5;
}

.ri-result-photos {
  flex-wrap: wrap;
  gap: 8px;
}

.ri-result-photo {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #E0E8EF;
  display: block;
  flex-shrink: 0;
}

/* ── Transitions ───────────────────────────── */
.ri-fade-enter-active { transition: opacity 0.22s; }
.ri-fade-enter        { opacity: 0; }
.ri-fade-leave-active { transition: opacity 0.18s; }
.ri-fade-leave-to     { opacity: 0; }
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/ReportIssue.vue